export type Layer = 'sensory' | 'integration' | 'symbolic';
export type Stage = 0 | 1 | 2 | 3 | 4;

export type VizType =
  | 'edge-simplifier' | 'waveform-word' | 'memory-spike' | 'state-gauge'
  | 'stability-field' | 'prediction-input' | 'urgency-amplifier' | 'valence-mapper'
  | 'story-arc' | 'reinforcement-loop' | 'attention-spotlight'
  | 'attention-sankey' | 'time-ledger' | 'proxy-reality' | 'procedure-collapse'
  | 'rank-ladder' | 'detail-funnel' | 'narrative-lens'
  | 'system-graph' | 'tradeoff-balance' | 'freeze-meter';

export interface HotspotContent {
  excerpt: string;
  teaching: string;
  tryThis?: string;
}

export interface Hotspot {
  id: string;
  layer: Layer;
  stage: Stage;
  label: string;
  short: string;
  position: { xPct: number; yPct: number };
  icon: string;
  content: HotspotContent;
  relatedIds?: string[];
  overlayWords?: string[];
  viz?: { type: VizType; props?: Record<string, unknown> };
}

export const layerInfo: Record<Layer, { name: string; description: string; color: string }> = {
  sensory: {
    name: 'Sensory Notation',
    description: 'Before symbols, there is signal. Raw perception is already compression.',
    color: '#2493A2'
  },
  integration: {
    name: 'Human Integration',
    description: 'Signals become meaning inside a living organism. Body and mind make sense together.',
    color: '#57BDA2'
  },
  symbolic: {
    name: 'Symbolic Systems',
    description: 'Meaning hardens into tools, norms, and institutions. Culture encodes behavior.',
    color: '#DCB13C'
  }
};

export const stageInfo: Record<Stage, {
  name: string;
  label: string;
  description: string;
  color: string;
  requiredExploreCount: number;
  uiLine?: string;
  unlockMessage?: string;
}> = {
  0: {
    name: 'Orientation',
    label: 'Begin',
    description: 'You do not experience reality directly.',
    color: '#8a8fa3',
    requiredExploreCount: 0
  },
  1: {
    name: 'Sensory Compression',
    label: 'Perception',
    description: 'Raw perception is already compression.',
    color: '#2493A2',
    requiredExploreCount: 3,
    uiLine: 'Signal \u2192 Compression \u2192 Perception',
    unlockMessage: 'You noticed compression.'
  },
  2: {
    name: 'Meaning & Identity',
    label: 'Meaning',
    description: 'Sensation becomes emotion, identity, and urgency.',
    color: '#57BDA2',
    requiredExploreCount: 3,
    uiLine: 'Meaning forms before choice.',
    unlockMessage: 'You saw meaning form before choice.'
  },
  3: {
    name: 'Symbolic Systems',
    label: 'Symbol',
    description: 'Meaning hardens into systems.',
    color: '#DCB13C',
    requiredExploreCount: 3,
    uiLine: 'Internal meaning \u2192 External symbol \u2192 Behavioral pressure',
    unlockMessage: 'You saw systems are frozen choices.'
  },
  4: {
    name: 'Integration & Responsibility',
    label: 'Responsibility',
    description: 'Authorship returns to you.',
    color: '#E85D75',
    requiredExploreCount: 0
  }
};

// STAGE 1: SENSORY COMPRESSION
export const sensoryHotspots: Hotspot[] = [
  {
    id: 'brain-sensory',
    layer: 'sensory',
    stage: 1,
    label: 'Brain & Nervous System',
    short: 'Prediction & Stability',
    position: { xPct: 50, yPct: 3 },
    icon: 'fa-brain',
    viz: { type: 'prediction-input' },
    content: {
      excerpt: '"The nervous system does not seek truth; it seeks stability."',
      teaching: 'The brain and nervous system are predictive engines. Incoming sensory data is immediately compared against prior models to decide what matters and what can be ignored. This compression favors speed and continuity over accuracy. Reality is not perceived as it is, but as it is expected to be. Identity itself emerges as a long-running prediction the system works to preserve.',
      tryThis: 'Notice a moment when your expectation overrode what you actually perceived. The brain filled in the gaps before you could look closely.'
    },
    relatedIds: ['sight', 'hearing', 'head-integration'],
    overlayWords: ['predict', 'expect', 'stable', 'model', 'filter']
  },
  {
    id: 'touch',
    layer: 'sensory',
    stage: 1,
    label: 'Touch',
    short: 'Boundary & Control',
    position: { xPct: 18, yPct: 58 },
    icon: 'fa-hand',
    content: {
      excerpt: '"Touch teaches the body where it ends and the world begins."',
      teaching: 'Touch compresses the physical world into boundaries: self versus other, safe versus unsafe. It grounds reality in immediacy and control. What can be touched feels more real than what cannot. This is essential for survival\u2014but it quietly teaches that only what can be grasped or manipulated truly matters.',
      tryThis: 'Touch something without naming it. Feel texture, temperature, weight\u2014before your mind adds meaning.'
    },
    relatedIds: ['hands-integration', 'interoception'],
    overlayWords: ['boundary', 'self', 'other', 'grasp', 'real']
  },
  {
    id: 'sight',
    layer: 'sensory',
    stage: 1,
    label: 'Sight',
    short: 'Form & Categorization',
    position: { xPct: 28, yPct: 11 },
    icon: 'fa-eye',
    viz: { type: 'edge-simplifier' },
    content: {
      excerpt: '"Vision simplifies reality by carving it into objects."',
      teaching: 'Sight reduces overwhelming visual input into edges, shapes, motion, and recognizable forms. The mind labels these instantly: object, person, threat, opportunity. This allows action but hides complexity. What is visible feels primary; what is unseen feels unreal.',
      tryThis: 'Look at a familiar space and try to see shapes and colors before your mind names them. Notice how quickly labels return.'
    },
    relatedIds: ['eyes-integration', 'brain-sensory'],
    overlayWords: ['object', 'form', 'recognize', 'visible', 'real']
  },
  {
    id: 'hearing',
    layer: 'sensory',
    stage: 1,
    label: 'Hearing',
    short: 'Pattern & Language',
    position: { xPct: 72, yPct: 11 },
    icon: 'fa-ear-listen',
    viz: { type: 'waveform-word' },
    content: {
      excerpt: '"Sound becomes meaning only after interpretation."',
      teaching: 'Hearing transforms vibration into tone, rhythm, and language. Meaning is culturally encoded. The same sound can signal safety or danger depending on learned context. Hearing is never neutral\u2014it is social decoding.',
      tryThis: 'Listen to a conversation and notice how much "meaning" you add to tone before processing words.'
    },
    relatedIds: ['ears-integration', 'brain-sensory'],
    overlayWords: ['tone', 'pattern', 'decode', 'meaning', 'signal']
  },
  {
    id: 'smell',
    layer: 'sensory',
    stage: 1,
    label: 'Smell',
    short: 'Memory Compression',
    position: { xPct: 50, yPct: 19 },
    icon: 'fa-wind',
    viz: { type: 'memory-spike' },
    content: {
      excerpt: '"Smell collapses time faster than thought."',
      teaching: 'Smell bypasses rational analysis and triggers memory, emotion, and bodily state instantly. Past meaning floods the present. This is powerful\u2014and unreliable. What once meant danger may no longer apply.',
      tryThis: 'When a scent triggers memory, pause before the story. Notice the body state first.'
    },
    relatedIds: ['interoception', 'brain-sensory'],
    overlayWords: ['memory', 'past', 'trigger', 'instant', 'bypass']
  },
  {
    id: 'taste',
    layer: 'sensory',
    stage: 1,
    label: 'Taste',
    short: 'Preference & Reward',
    position: { xPct: 50, yPct: 28 },
    icon: 'fa-utensils',
    content: {
      excerpt: '"Taste reduces nourishment into preference."',
      teaching: 'Taste compresses complexity into desire and aversion. Over time, preference hardens into identity and comfort-seeking. Pleasure becomes narrative. Awareness allows enjoyment without compulsion.',
      tryThis: 'Eat something slowly. Notice when "delicious" or "not good" arrives\u2014that\'s the compression happening.'
    },
    relatedIds: ['interoception', 'gut-integration'],
    overlayWords: ['desire', 'prefer', 'reward', 'comfort', 'want']
  },
  {
    id: 'interoception',
    layer: 'sensory',
    stage: 1,
    label: 'Interoception',
    short: 'Internal State',
    position: { xPct: 50, yPct: 46 },
    icon: 'fa-heart-pulse',
    viz: { type: 'state-gauge' },
    content: {
      excerpt: '"The body knows your state before you do."',
      teaching: 'Internal sensations compress bodily condition into mood and readiness. When ignored, they escalate. When over-identified, they become identity. Awareness restores dialogue rather than domination.',
      tryThis: 'Right now, notice your internal state\u2014heartbeat, breathing, tension\u2014before naming an emotion.'
    },
    relatedIds: ['chest-integration', 'gut-integration'],
    overlayWords: ['state', 'mood', 'feel', 'internal', 'body']
  },
  {
    id: 'balance',
    layer: 'sensory',
    stage: 1,
    label: 'Balance & Orientation',
    short: 'Stability & Confidence',
    position: { xPct: 50, yPct: 82 },
    icon: 'fa-scale-balanced',
    viz: { type: 'stability-field' },
    content: {
      excerpt: '"Stability creates the illusion of certainty."',
      teaching: 'Physical balance influences emotional conviction. When the body feels stable, the world feels manageable. Disorientation often precedes insight.',
      tryThis: 'Notice how your sense of "certainty" about an idea correlates with physical groundedness.'
    },
    relatedIds: ['feet-integration', 'brain-sensory'],
    overlayWords: ['stable', 'certain', 'ground', 'steady', 'sure']
  }
];

// STAGE 2: MEANING & IDENTITY FORMATION
export const integrationHotspots: Hotspot[] = [
  {
    id: 'eyes-integration',
    layer: 'integration',
    stage: 2,
    label: 'Eyes',
    short: 'Attention & Selection',
    position: { xPct: 28, yPct: 11 },
    icon: 'fa-eye',
    viz: { type: 'attention-spotlight' },
    content: {
      excerpt: '"What you notice determines what exists for you."',
      teaching: 'Attention filters perception. What is noticed becomes real; what is ignored disappears. Attention is finite, and where it settles, meaning accumulates.',
      tryThis: 'Look around and notice what your eyes skip over. What exists in the room that you never "see"?'
    },
    relatedIds: ['sight', 'phone', 'mirror'],
    overlayWords: ['notice', 'select', 'focus', 'exist', 'ignore']
  },
  {
    id: 'ears-integration',
    layer: 'integration',
    stage: 2,
    label: 'Ears',
    short: 'Belonging & Threat',
    position: { xPct: 72, yPct: 11 },
    icon: 'fa-ear-listen',
    content: {
      excerpt: '"Tone often matters more than content."',
      teaching: 'Social meaning is inferred before facts are processed. Belonging and rejection are sensed, not reasoned.',
      tryThis: 'In a conversation, notice the moment you "know" how someone feels\u2014before they finish speaking.'
    },
    relatedIds: ['hearing', 'social-feed', 'mouth-integration'],
    overlayWords: ['belong', 'reject', 'tone', 'threat', 'safe']
  },
  {
    id: 'mouth-integration',
    layer: 'integration',
    stage: 2,
    label: 'Mouth',
    short: 'Narrative & Identity',
    position: { xPct: 50, yPct: 22 },
    icon: 'fa-comment',
    viz: { type: 'story-arc' },
    content: {
      excerpt: '"What you say is not neutral; it is authorship."',
      teaching: 'Speech stabilizes identity. Repeated narratives become selfhood. Language does not just describe experience\u2014it locks it into form.',
      tryThis: 'Notice the next time you say "I am..." \u2014 is that a fact, or a story you\'re reinforcing?'
    },
    relatedIds: ['ears-integration', 'head-integration', 'book'],
    overlayWords: ['I am', 'story', 'always', 'never', 'define']
  },
  {
    id: 'hands-integration',
    layer: 'integration',
    stage: 2,
    label: 'Hands',
    short: 'Agency & Ownership',
    position: { xPct: 82, yPct: 58 },
    icon: 'fa-hand-fist',
    content: {
      excerpt: '"To act is to believe something can be changed."',
      teaching: 'Hands turn perception into control. When agency becomes identity, inability to fix feels like failure.',
      tryThis: 'Notice when you feel helpless\u2014is the problem unsolvable, or just not solvable by doing?'
    },
    relatedIds: ['touch', 'checklist', 'phone'],
    overlayWords: ['control', 'fix', 'make', 'mine', 'do']
  },
  {
    id: 'chest-integration',
    layer: 'integration',
    stage: 2,
    label: 'Chest / Heart',
    short: 'Value Assignment',
    position: { xPct: 50, yPct: 38 },
    icon: 'fa-heart',
    viz: { type: 'valence-mapper' },
    content: {
      excerpt: '"Emotion assigns importance before logic arrives."',
      teaching: 'Emotion marks what matters. When emotion freezes into judgment, it stops informing choice and starts ruling it.',
      tryThis: 'When you feel strongly about something, pause: Is this a sensation, or has it already become a verdict?'
    },
    relatedIds: ['interoception', 'compass', 'dollar'],
    overlayWords: ['matters', 'feel', 'care', 'value', 'important']
  },
  {
    id: 'gut-integration',
    layer: 'integration',
    stage: 2,
    label: 'Gut',
    short: 'Urgency & Safety',
    position: { xPct: 50, yPct: 52 },
    icon: 'fa-shield-halved',
    viz: { type: 'urgency-amplifier' },
    content: {
      excerpt: '"Urgency feels real because the body believes it."',
      teaching: 'The gut reduces complexity into immediate action cues. In modern life, it often misfires\u2014turning abstraction into threat.',
      tryThis: 'When your gut tightens, ask: Is there actual danger\u2014or just a symbol my body treats as threat?'
    },
    relatedIds: ['interoception', 'phone', 'calendar'],
    overlayWords: ['urgent', 'danger', 'now', 'threat', 'react']
  },
  {
    id: 'feet-integration',
    layer: 'integration',
    stage: 2,
    label: 'Feet',
    short: 'Place & Belonging',
    position: { xPct: 50, yPct: 92 },
    icon: 'fa-shoe-prints',
    content: {
      excerpt: '"Where you stand quietly decides how you think."',
      teaching: 'Place compresses identity and possibility. Movement threatens coherence when place becomes destiny.',
      tryThis: 'Feel your feet on the ground right now. Notice how rarely you inhabit them during a typical day.'
    },
    relatedIds: ['balance', 'compass'],
    overlayWords: ['ground', 'belong', 'place', 'here', 'home']
  },
  {
    id: 'head-integration',
    layer: 'integration',
    stage: 2,
    label: 'Head',
    short: 'Modeling & Identity Loop',
    position: { xPct: 50, yPct: 3 },
    icon: 'fa-lightbulb',
    viz: { type: 'reinforcement-loop' },
    content: {
      excerpt: '"The mind prefers continuity over truth."',
      teaching: 'Identity is a model the brain protects. Awareness allows revision without collapse.',
      tryThis: 'Notice a belief you hold tightly. Ask: Does this serve understanding, or does it protect my sense of self?'
    },
    relatedIds: ['brain-sensory', 'mirror', 'book'],
    overlayWords: ['think', 'believe', 'know', 'self', 'always']
  }
];

// STAGE 3: SYMBOLIC SYSTEMS
export const symbolicHotspots: Hotspot[] = [
  {
    id: 'phone',
    layer: 'symbolic',
    stage: 3,
    label: 'Smartphone',
    short: 'Attention Extraction',
    position: { xPct: 12, yPct: 35 },
    icon: 'fa-mobile-screen',
    viz: { type: 'attention-sankey' },
    content: {
      excerpt: '"What captures attention quietly becomes valuable."',
      teaching: 'The phone fragments attention into urgency and comparison. Every notification is engineered to feel important. Attention becomes currency\u2014extracted, traded, and monetized. Your nervous system cannot distinguish manufactured urgency from real need.',
      tryThis: 'Turn off your phone for 30 minutes. Notice the phantom urges, the sense of missing something that didn\'t exist before the device.'
    },
    relatedIds: ['eyes-integration', 'gut-integration', 'social-feed'],
    overlayWords: ['check', 'urgent', 'now', 'missing', 'notify']
  },
  {
    id: 'calendar',
    layer: 'symbolic',
    stage: 3,
    label: 'Clock / Calendar',
    short: 'Time as Judgment',
    position: { xPct: 88, yPct: 20 },
    icon: 'fa-calendar-days',
    viz: { type: 'time-ledger' },
    content: {
      excerpt: '"Time becomes oppressive when it becomes moral."',
      teaching: 'Time compresses continuous duration into discrete, scarce units. It can be "spent," "wasted," "saved," or "lost." Productivity becomes moral performance. Rest feels like theft. Worth becomes measured in output per hour.',
      tryThis: 'Spend 10 minutes without checking time. Notice how often your mind wants to know "how long" something is taking.'
    },
    relatedIds: ['gut-integration', 'checklist', 'dollar'],
    overlayWords: ['late', 'wasted', 'behind', 'deadline', 'hurry']
  },
  {
    id: 'dollar',
    layer: 'symbolic',
    stage: 3,
    label: 'Money / Receipt',
    short: 'Value Proxy',
    position: { xPct: 85, yPct: 55 },
    icon: 'fa-dollar-sign',
    viz: { type: 'proxy-reality' },
    content: {
      excerpt: '"Money is a promise about value, not value itself."',
      teaching: 'Money compresses incommensurable things\u2014labor, time, talent, care\u2014into a single exchangeable unit. This enables coordination but confuses price with value, and net worth with self-worth. What cannot be priced becomes invisible.',
      tryThis: 'Think of something you value that has no market price. Notice how hard it is to "justify" time spent on it.'
    },
    relatedIds: ['chest-integration', 'calendar', 'checklist'],
    overlayWords: ['worth', 'afford', 'cost', 'price', 'value']
  },
  {
    id: 'chalkboard',
    layer: 'symbolic',
    stage: 3,
    label: 'Chalkboard / Math',
    short: 'Precision Through Reduction',
    position: { xPct: 90, yPct: 38 },
    icon: 'fa-square-root-variable',
    viz: { type: 'detail-funnel' },
    content: {
      excerpt: '"Clarity is achieved by losing detail."',
      teaching: 'Formal notation (math, logic, code) compresses complex relationships into manipulable symbols. This enables precision, proof, and technology. But when applied to human life, it often measures the measurable while ignoring the meaningful.',
      tryThis: 'Notice something you experience that can\'t be put into numbers. What happens when institutions try anyway?'
    },
    relatedIds: ['head-integration', 'checklist'],
    overlayWords: ['measure', 'prove', 'calculate', 'reduce', 'exact']
  },
  {
    id: 'book',
    layer: 'symbolic',
    stage: 3,
    label: 'Literature / Book',
    short: 'Narrative Meaning',
    position: { xPct: 10, yPct: 55 },
    icon: 'fa-book',
    viz: { type: 'narrative-lens' },
    content: {
      excerpt: '"Stories teach us how to feel reality."',
      teaching: 'Stories compress experience into transmissible meaning. We inherit narratives about what life is, what matters, what a good person does. These become templates for living\u2014often without examination. The story you tell about yourself shapes what you allow yourself to become.',
      tryThis: 'Notice a story you tell about your life. What details do you leave out? What does the editing reveal?'
    },
    relatedIds: ['mouth-integration', 'head-integration', 'mirror'],
    overlayWords: ['story', 'hero', 'meaning', 'lesson', 'narrative']
  },
  {
    id: 'checklist',
    layer: 'symbolic',
    stage: 3,
    label: 'Checklist',
    short: 'Procedure & Compliance',
    position: { xPct: 15, yPct: 72 },
    icon: 'fa-list-check',
    viz: { type: 'procedure-collapse' },
    content: {
      excerpt: '"When meaning becomes procedure, responsibility thins."',
      teaching: 'Checklists compress complex judgment into binary: done/not done. This enables coordination and reduces error\u2014but creates "completion identity." Checking boxes feels like progress. What cannot be checked stops mattering.',
      tryThis: 'Notice what you do that never makes it onto a list. Is it less valuable\u2014or just less measurable?'
    },
    relatedIds: ['hands-integration', 'calendar', 'dollar'],
    overlayWords: ['done', 'todo', 'complete', 'check', 'finish']
  },
  {
    id: 'compass',
    layer: 'symbolic',
    stage: 3,
    label: 'Compass',
    short: 'Purpose & Direction',
    position: { xPct: 82, yPct: 75 },
    icon: 'fa-compass',
    content: {
      excerpt: '"A direction simplifies life by excluding alternatives."',
      teaching: 'The compass compresses infinite directions into four\u2014and creates "North" as destination. We apply this to life: find your "true north," your "purpose," your "calling." But purpose may be more fluid than the notation allows. Direction can become prison when treated as destiny.',
      tryThis: 'Consider that "finding your purpose" may be a notation\u2014useful, but not necessarily how meaning works.'
    },
    relatedIds: ['feet-integration', 'chest-integration'],
    overlayWords: ['purpose', 'direction', 'calling', 'path', 'north']
  },
  {
    id: 'mirror',
    layer: 'symbolic',
    stage: 3,
    label: 'Mirror',
    short: 'Self-Notation',
    position: { xPct: 8, yPct: 18 },
    icon: 'fa-id-card',
    content: {
      excerpt: '"The self you see is already edited."',
      teaching: 'The mirror converts continuous becoming into fixed image. You see "yourself"\u2014but what you see is already a notation: "how I look," "who I am," "what they see." Self-image becomes self-concept becomes self-limitation. The person you see is a 2D still frame of a 4D process.',
      tryThis: 'Look in a mirror without evaluating. Just see shapes, colors, textures\u2014before "you" appear.'
    },
    relatedIds: ['eyes-integration', 'social-feed', 'head-integration'],
    overlayWords: ['image', 'self', 'look', 'appear', 'identity']
  },
  {
    id: 'social-feed',
    layer: 'symbolic',
    stage: 3,
    label: 'Social Feed',
    short: 'Status & Belonging',
    position: { xPct: 22, yPct: 25 },
    icon: 'fa-users',
    viz: { type: 'rank-ladder' },
    content: {
      excerpt: '"Belonging is often purchased with comparison."',
      teaching: 'Social feeds compress human lives into highlight reels optimized for engagement. Comparison becomes constant, status becomes quantified. You compare your inside to their outside. Visibility becomes worth. A "like" from a stranger can change your mood\u2014a notation that didn\'t exist 20 years ago.',
      tryThis: 'After scrolling, notice your emotional state. Did you just compare your unedited life to edited glimpses?'
    },
    relatedIds: ['ears-integration', 'phone', 'mirror'],
    overlayWords: ['like', 'compare', 'follow', 'seen', 'status']
  }
];

export const allHotspots: Hotspot[] = [...sensoryHotspots, ...integrationHotspots, ...symbolicHotspots];

export function getHotspotById(id: string): Hotspot | undefined {
  return allHotspots.find(h => h.id === id);
}

export function getHotspotsByLayer(layer: Layer): Hotspot[] {
  return allHotspots.filter(h => h.layer === layer);
}

export function getHotspotsByStage(stage: Stage): Hotspot[] {
  return allHotspots.filter(h => h.stage === stage);
}

// Stage-aware guided prompts
export const stageGuidedPrompts: Record<Stage, { hotspotId: string; prompt: string }[]> = {
  0: [],
  1: [
    { hotspotId: 'sight', prompt: 'How does vision carve reality into objects?' },
    { hotspotId: 'hearing', prompt: 'When does sound become meaning?' },
    { hotspotId: 'smell', prompt: 'Notice how smell collapses time.' },
    { hotspotId: 'interoception', prompt: 'What does the body know before the mind names it?' },
    { hotspotId: 'balance', prompt: 'How does physical stability become mental certainty?' },
    { hotspotId: 'brain-sensory', prompt: 'The brain predicts before it perceives.' },
    { hotspotId: 'touch', prompt: 'Where does the body end and the world begin?' },
    { hotspotId: 'taste', prompt: 'When does nourishment become preference?' },
  ],
  2: [
    { hotspotId: 'gut-integration', prompt: 'Notice how urgency appears before thought.' },
    { hotspotId: 'chest-integration', prompt: 'Emotion assigns value before logic arrives.' },
    { hotspotId: 'mouth-integration', prompt: 'Notice how language stabilizes identity.' },
    { hotspotId: 'head-integration', prompt: 'The mind prefers continuity over truth.' },
    { hotspotId: 'eyes-integration', prompt: 'What you notice determines what exists.' },
    { hotspotId: 'ears-integration', prompt: 'Belonging is sensed, not reasoned.' },
    { hotspotId: 'hands-integration', prompt: 'Agency becomes identity.' },
    { hotspotId: 'feet-integration', prompt: 'Place quietly decides how you think.' },
  ],
  3: [
    { hotspotId: 'phone', prompt: 'What captures attention becomes valuable.' },
    { hotspotId: 'calendar', prompt: 'When is "wasted time" a judgment, not a fact?' },
    { hotspotId: 'dollar', prompt: 'What cannot be priced\u2014and therefore disappears?' },
    { hotspotId: 'chalkboard', prompt: 'Precision is achieved by losing detail.' },
    { hotspotId: 'book', prompt: 'Stories teach us how to feel reality.' },
    { hotspotId: 'checklist', prompt: 'When meaning becomes procedure, responsibility thins.' },
    { hotspotId: 'social-feed', prompt: 'Belonging is often purchased with comparison.' },
    { hotspotId: 'mirror', prompt: 'The self you see is already edited.' },
    { hotspotId: 'compass', prompt: 'Direction simplifies life by excluding alternatives.' },
  ],
  4: []
};
